import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, DOCUMENT, HostListener, inject, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { ApmService } from '@elastic/apm-rum-angular';
import { NgxLoadingBar } from '@ngx-loading-bar/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthenticationApiService } from '@warema/shared/authentication/api';
import { UserCentricsService } from '@warema/shared/consent';
import { ENVIRONMENT, StorageConstants, WaremaPlusEnvironment, WorkflowOrigin } from '@warema/shared/models';
import { AppVersionService } from '@warema/shared/scaffold';
import { CountryService, SunCenterService } from '@warema/shared/services';
import { SharedHeaderMenuComponent } from '@warema/shared/shared-header-menu';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { MetaRouteKeysWaremaPlus } from '@warema/warema-plus/models';
import { AppStateService, MenuItemType, MenuService, MetaService } from '@warema/warema-plus/scaffold';
import { WaremaIcon } from '@warema/warema-ui';
import {
    WaremaAppFrame,
    WaremaHeader,
    WaremaHeaderButton,
    WaremaHeaderMenu,
    WaremaHeaderMenuItem,
    WaremaSideNavigationBar,
    WaremaSideNavigationBarDivider,
    WaremaSideNavigationBarItem,
} from '@warema/warema-ui-angular';
import { delay, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'warema-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        AsyncPipe,
        TranslatePipe,
        RouterLink,
        RouterOutlet,
        SharedHeaderMenuComponent,
        NgxLoadingBar,
        MatBadge,
        WaremaAppFrame,
        WaremaHeader,
        WaremaHeaderButton,
        WaremaHeaderMenu,
        WaremaHeaderMenuItem,
        WaremaSideNavigationBar,
        WaremaSideNavigationBarDivider,
        WaremaSideNavigationBarItem,
    ],
})
export class AppComponent implements OnInit {
    private readonly _environment = inject<WaremaPlusEnvironment>(ENVIRONMENT);
    public readonly authenticationApiService = inject(AuthenticationApiService);
    public readonly translateService = inject(TranslateService);
    public readonly appStateService = inject(AppStateService);
    public readonly menuService = inject(MenuService);
    public readonly userCentricsService = inject(UserCentricsService);
    public readonly sunCenterService = inject(SunCenterService);
    public readonly tenantConfig = inject(TenantConfigurationService);
    private readonly _document = inject<Document>(DOCUMENT);
    private readonly _router = inject(Router);
    private readonly _route = inject(ActivatedRoute);
    private readonly _renderer = inject(Renderer2);
    private readonly _countryService = inject(CountryService);
    private readonly _metaService = inject(MetaService);
    private readonly _apmService = inject(ApmService);
    private readonly _appVersionService = inject(AppVersionService);
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _deLang = 'DE';

    public isMenuRestrictedRoute: boolean;
    public isShopRoute = false;
    public hideHeader = false;
    public hideMenu = false;
    public showMobileMenuEntries = false;
    public buildVersion: string | null;

    public readonly menuItemType = MenuItemType;

    constructor() {
        this._handleMobileMenuEntriesOnResize();

        if (this._environment.apm.active) {
            console.log('Initializing APM Service with config:', this._environment.apm);
            this._apmService.init(this._environment.apm);
            this._apmService.apm.addLabels({
                appVersion: this._environment.buildVersion ?? '',
            });
        }
        this.buildVersion = this._environment.buildVersion;

        // eslint-disable-next-line sonarjs/no-async-constructor
        void WaremaIcon.changeIconPath('assets/generated-icons');
    }

    @HostListener('window:resize')
    protected _handleMobileMenuEntriesOnResize(): void {
        this.showMobileMenuEntries = window.innerWidth < 1024;
    }

    public ngOnInit(): void {
        void this.authenticationApiService.init();
        this._appVersionService.setupReoccuringAppVersionCheck();

        this._handleRouteChange();

        this.translateService.addLangs(this.tenantConfig.settings.languages);
        this.appStateService.countrySearch$.next(this.tenantConfig.settings.country);

        this._setAppLanguage();
        this._manageLangQueryParameter();
        this._manageLoggedInUser();

        this.translateService.onLangChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(langChangeEvent => {
            this._renderer.setAttribute(document.querySelector('html'), 'lang', langChangeEvent.lang);
            localStorage.setItem(StorageConstants.LANGUAGE_KEY, langChangeEvent.lang);
        });

        this.authenticationApiService.user$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
            .subscribe(user => {
                const settings = this.tenantConfig.settings;
                const countryCodeSelection = user?.country || settings.country;
                const countrySelectionConfig = settings.countryConfigMap.get(countryCodeSelection);
                this._countryService.persistCountrySelection(countrySelectionConfig, !user);

                if (settings.country !== countryCodeSelection) {
                    location.reload();
                }
            });

        this._setMetaTitleAndDescription();
    }

    private _setMetaTitleAndDescription(): void {
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this._route),
                map(route => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                switchMap(route => route.data),
                takeUntilDestroyed(this._destroyRef),
                tap((data: Data) => {
                    const metaKey = data['metaKey'] as MetaRouteKeysWaremaPlus;
                    this._metaService.updateMetaBasedOnMetaRouteKey(metaKey);
                }),
            )
            .subscribe();
    }

    private _handleRouteChange(): void {
        this.appStateService
            .getCurrentRoute()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(route => {
                this.isShopRoute = route.startsWith('/shop');
                this.isMenuRestrictedRoute = this._isMenuRestrictedByRoute(route);

                const originQueryParam = this._route.snapshot.queryParams['origin'] as WorkflowOrigin;
                this.hideHeader = [WorkflowOrigin.SunCenter, WorkflowOrigin.WaremaCom].includes(originQueryParam);
                this.hideMenu = [WorkflowOrigin.WaremaCom].includes(originQueryParam);
            });
    }

    private _setAppLanguage(): void {
        const storedLang = localStorage.getItem(StorageConstants.LANGUAGE_KEY);
        const browserLangOrStoredLang = storedLang ?? this.translateService.getBrowserLang() ?? null;

        if (browserLangOrStoredLang) {
            this._setLanguage(browserLangOrStoredLang);
        }
    }

    private _manageLangQueryParameter(): void {
        this._route.queryParams
            .pipe(
                filter(f => f && !!(f['lang'] as string)),
                first(),
                delay(250),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(params => {
                const language = this._setLanguage(params['lang'] as string);
                this._changeLangQueryParam(language);
            });
    }

    private _setLanguage(lang: string): string {
        const settings = this.tenantConfig.settings;
        const searchedLanguage = settings.languages.find(x => x === lang || x.includes(lang));
        const deLanguage = settings.languages.find(x => x === this._deLang);
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        const language = searchedLanguage || deLanguage || settings.languages[0];

        this.translateService.use(language);
        this._document.documentElement.lang = language;
        return language;
    }

    private _manageLoggedInUser(): void {
        this.authenticationApiService.loggedIn$
            .pipe(
                filter(loggedIn => loggedIn),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe(() => this.menuService.setNumberOfInProgressAppointments());
    }

    private _changeLangQueryParam(language: string): void {
        void this._router.navigate([], {
            relativeTo: this._route,
            queryParams: { lang: language },
            queryParamsHandling: 'merge',
        });
    }

    private _isMenuRestrictedByRoute(route: string): boolean {
        return (
            route.startsWith('/appointment-request') ||
            route.startsWith('/products-registration') ||
            route.startsWith('/product-registration/wizard') ||
            route.startsWith('/error') ||
            route.startsWith('/serviceappointment')
        );
    }

    public handleClickRouteRedirect(targetRouteKey: string): void {
        if (targetRouteKey === 'warema-plus-company-search') {
            this.menuService.trackDealerMenuButton();
        }
    }
}
